import {
  CREATED_BY_CLOUD_SHELL,
  P0_TAG_NAME,
} from "@p0security/integrations-aws-shared/constants";
import {
  buildGcpP0TrustPolicy,
  envSuffixFor,
} from "@p0security/integrations-aws-shared/policy/shared";
import type { DevEnv } from "@p0security/shared/types/environment";

export const midcRoleName = (context: DevEnv): string =>
  `P0RoleMergedIdc${envSuffixFor(context)}`;

export const midcInlinePolicyName = (context: DevEnv): string =>
  `${midcRoleName(context)}Policy`;

export const midcInstallCommands = (
  context: DevEnv,
  args: { gcloudServiceAccountId: string; awsAccountId: string }
): string => {
  const roleName = midcRoleName(context);
  const trustPolicy = buildGcpP0TrustPolicy(args.gcloudServiceAccountId);
  const trustPolicyJson = JSON.stringify(trustPolicy, undefined, 2);
  const policy = midcInlinePolicy();
  return `export CALLER=$(aws sts get-caller-identity --query Arn --output text) && \\
aws iam create-role \\
  --tags Key="${P0_TAG_NAME}",Value="${CREATED_BY_CLOUD_SHELL} by \${CALLER}" \\
  --role-name "${roleName}" \\
  --assume-role-policy-document '${trustPolicyJson}' \\
  && \\
aws iam put-role-policy \\
  --role-name "${roleName}" \\
  --policy-name "${midcInlinePolicyName(context)}" \\
  --policy-document '${JSON.stringify(policy, undefined, 2)}'`;
};

/**
 * The inline policy P0 shipped before it could grant access to the account
 * that hosts Identity Center. The verifier still accepts it so existing
 * installs keep working until the customer reruns the setup commands.
 */
export const legacyMidcInlinePolicy = () => ({
  Version: "2012-10-17",
  Statement: [
    {
      Sid: "P0CanVerifyOwnRole",
      Effect: "Allow",
      Action: ["iam:GetRole", "iam:GetRolePolicy"],
      Resource: "*",
    },
    {
      Sid: "P0CanManageSsoAssignments",
      Effect: "Allow",
      Action: [
        "identitystore:ListUsers",
        "sso:AttachCustomerManagedPolicyReferenceToPermissionSet",
        "sso:AttachManagedPolicyToPermissionSet",
        "sso:CreateAccountAssignment",
        "sso:CreatePermissionSet",
        "sso:DeleteAccountAssignment",
        "sso:DeletePermissionSet",
        "sso:DescribeAccountAssignmentCreationStatus",
        "sso:DescribeAccountAssignmentDeletionStatus",
        "sso:DescribePermissionSet",
        "sso:DescribePermissionSetProvisioningStatus",
        "sso:DetachCustomerManagedPolicyReferenceFromPermissionSet",
        "sso:DetachManagedPolicyFromPermissionSet",
        "sso:GetInlinePolicyForPermissionSet",
        "sso:ListAccountAssignments",
        "sso:ListCustomerManagedPolicyReferencesInPermissionSet",
        "sso:ListInstances",
        "sso:ListManagedPoliciesInPermissionSet",
        "sso:ListPermissionSets",
        "sso:ListTagsForResource",
        "sso:ProvisionPermissionSet",
        "sso:PutInlinePolicyToPermissionSet",
        "sso:TagResource",
      ],
      Resource: "*",
    },
  ],
});

export const midcInlinePolicy = (): object => ({
  Version: "2012-10-17",
  Statement: [
    ...legacyMidcInlinePolicy().Statement,
    {
      Sid: "P0CanManageSsoRolesInIdcAccount",
      Effect: "Allow",
      Action: [
        "iam:AttachRolePolicy",
        "iam:CreateRole",
        "iam:DeleteRole",
        "iam:DeleteRolePolicy",
        "iam:DetachRolePolicy",
        "iam:ListAttachedRolePolicies",
        "iam:ListRolePolicies",
        "iam:PutRolePolicy",
      ],
      // Identity Center makes these calls with the caller's credentials, not its
      // service-linked role, when the assignment targets the account that hosts
      // it. Limiting them to the SSO-reserved path keeps P0 from creating or
      // changing any other role; slashes aren't allowed in role names, so a
      // role can't fake this path.
      Resource: "arn:*:iam::*:role/aws-reserved/sso.amazonaws.com/*",
    },
    {
      Sid: "P0CanReadSsoSamlProvider",
      Effect: "Allow",
      Action: "iam:GetSAMLProvider",
      Resource: "arn:*:iam::*:saml-provider/AWSSSO_*",
    },
  ],
});
